import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AlertCircle, CheckCircle2, ChevronDown, ChevronRight, Link2, Loader2,
  Megaphone, RefreshCw, Send, Users,
} from "lucide-react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api from "@/lib/api";
import { errorCode, errorMessage } from "@/lib/errors";
import { formatDateTime } from "@/lib/dates";

/**
 * Admin broadcasts: one notification, one audience, one or both languages.
 *
 * **The count is a step, not a decoration.** Sending is two requests on
 * purpose — estimate, then send — because the number of people about to be
 * interrupted is the single fact that should change an admin's mind, and a
 * confirmation that appears after the send has changed nothing. The estimate is
 * recorded with the broadcast, so what was on the screen stays answerable.
 *
 * **And it has to be a count of devices that exist for OneSignal, not for us.**
 * On 2026-08-21 this screen said "5 registered devices" whatever audience was
 * picked, and OneSignal's own answer was that four of the five belonged to no
 * account: reachable by "everyone", invisible to every filter by role, plan or
 * category. The number was not wrong about our database — it was answering a
 * different question from the one being asked. So the screen shows both halves:
 * how many devices the audience names, how many of those OneSignal says it can
 * reach, and what the difference is made of. When the check could not run it
 * says that too, instead of quietly presenting the larger number as the
 * promise.
 *
 * **A count is still not an answer to "who".** Behind it, collapsed, is the
 * list of people — name, role, and the language each of them will read the
 * message in — paged in the database because an audience is allowed to be
 * everyone.
 *
 * **One language is enough (2026-09-02).** Both were required for as long as
 * the send handed both texts to OneSignal and let it pick per device from the
 * language the *browser* had recorded. Delivery follows `user_profiles.language`
 * now, so the choice is ours: write both and each person reads their own; write
 * one and everybody reads that one. Demanding a translation was making silence
 * the price of writing in Spanish.
 *
 * Deliberately without scheduling and without templates — Jorge scoped both to
 * post-launch. Deliberately without deletion, which is not a gap: this list is
 * the audit trail of who addressed the whole platform, and it is not the
 * panel's to tidy away.
 */
const AUDIENCES = ["all", "clients", "providers", "tier", "category"];
const TIERS = ["starter", "pro", "master"];
const HISTORY_PAGE = 10;
const RECIPIENTS_PAGE = 10;

// The same rule as `_TARGET_PATH` in routes/admin.py and as
// `push_broadcasts_target_internal` in the schema. Here for the message, not
// for the enforcement: the server refuses it whatever this says.
const INTERNAL_PATH = /^\/(?!\/)[^\\\s]*$/;

const EMPTY = {
  title_en: "", title_es: "", body_en: "", body_es: "",
  audience: "all", audience_value: "", target_path: "",
};

export default function AdminBroadcasts() {
  const { t } = useTranslation();
  const [form, setForm] = useState(EMPTY);
  const [categories, setCategories] = useState([]);
  // Three states, not two: "not loaded yet" and "could not be loaded" are
  // different things to show, and collapsing them into an empty array is
  // exactly how this selector spent weeks silently offering nothing.
  const [categoriesState, setCategoriesState] = useState("loading");
  const [estimate, setEstimate] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(null);
  const [error, setError] = useState("");
  const [history, setHistory] = useState([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historySkip, setHistorySkip] = useState(0);

  const loadHistory = useCallback(async (skip = 0) => {
    try {
      const { data } = await api.get(
        `/admin/broadcasts?limit=${HISTORY_PAGE}&skip=${skip}`
      );
      setHistory(data.broadcasts || []);
      setHistoryTotal(data.total ?? (data.broadcasts || []).length);
      setHistorySkip(skip);
    } catch (e) {
      console.error("[admin] could not load broadcast history:", e);
    }
  }, []);

  /**
   * The category catalog.
   *
   * `/categories` until 2026-09-02, which is not a route this API has — the
   * router carries a `/catalog` prefix and the other eight callers all use it.
   * The 404 landed in a `.catch` that set an empty list, so the selector
   * offered nothing and said nothing, and the audience it gates could not be
   * chosen at all. A fetch that fails now says so and offers to try again.
   */
  const loadCategories = useCallback(async () => {
    setCategoriesState("loading");
    try {
      const { data } = await api.get("/catalog/categories");
      setCategories(data.categories || []);
      setCategoriesState("ready");
    } catch (e) {
      console.error("[admin] could not load the category catalog:", e);
      setCategories([]);
      setCategoriesState("error");
    }
  }, []);

  useEffect(() => {
    loadHistory(0);
    loadCategories();
  }, [loadHistory, loadCategories]);

  const set = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    // Any edit invalidates the number the admin was shown: confirming against a
    // stale count is the one mistake this two-step flow exists to prevent.
    setEstimate(null);
    setSent(null);
    setError("");
  };

  const trimmed = (key) => form[key].trim();
  const whole = (lang) => !!(trimmed(`title_${lang}`) && trimmed(`body_${lang}`));
  const half = (lang) => !!trimmed(`title_${lang}`) !== !!trimmed(`body_${lang}`);
  const languageOk = (whole("en") || whole("es")) && !half("en") && !half("es");
  const targetOk = !form.target_path.trim()
    || form.target_path.trim() === "/"
    || INTERNAL_PATH.test(form.target_path.trim());

  const needsValue = form.audience === "tier" || form.audience === "category";
  const complete = languageOk && targetOk && (!needsValue || form.audience_value);

  const ask = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/admin/broadcasts/estimate", payload(form));
      // The whole answer, not just the headline number: what it excluded and
      // whether it was checked are the parts that make the number mean anything.
      setEstimate(data);
    } catch (e) {
      setError(refusal(e, t));
    } finally {
      setBusy(false);
    }
  };

  const send = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/admin/broadcasts", payload(form));
      setSent(data);
      setEstimate(null);
      setForm(EMPTY);
      await loadHistory(0);
    } catch (e) {
      setError(refusal(e, t));
      // A failed send is recorded too, so the trail is the thing to re-read.
      await loadHistory(0);
    } finally {
      setBusy(false);
    }
  };

  return (
    <DashboardLayout role="admin">
      <div data-testid="admin-broadcasts">
        <div className="flex items-center gap-3 mb-2">
          <Megaphone size={22} className="text-hive-ink" />
          <h1 className="font-display text-3xl font-bold text-worker">
            {t("adminBroadcasts.title")}
          </h1>
        </div>
        <p className="text-smoke mb-8 max-w-2xl">{t("adminBroadcasts.intro")}</p>

        <div className="card-base p-6 max-w-3xl">
          <Pair
            label={t("adminBroadcasts.titleField")}
            en={form.title_en} es={form.title_es}
            onEn={(v) => set("title_en", v)} onEs={(v) => set("title_es", v)}
            maxLength={64} testid="broadcast-title" t={t}
          />
          <Pair
            label={t("adminBroadcasts.bodyField")}
            en={form.body_en} es={form.body_es}
            onEn={(v) => set("body_en", v)} onEs={(v) => set("body_es", v)}
            maxLength={240} textarea testid="broadcast-body" t={t}
          />
          {!languageOk && (
            <p className="mt-2 text-xs text-smoke" data-testid="broadcast-language-hint">
              {half("en") || half("es")
                ? t("adminBroadcasts.languageIncomplete")
                : t("adminBroadcasts.needLanguage")}
            </p>
          )}
          {languageOk && !(whole("en") && whole("es")) && (
            <p className="mt-2 text-xs text-smoke" data-testid="broadcast-fallback-hint">
              {t("adminBroadcasts.oneLanguageReaches", {
                language: t(`adminBroadcasts.langs.${whole("es") ? "es" : "en"}`),
              })}
            </p>
          )}

          <div className="mt-5">
            <label className="label-base" htmlFor="broadcast-target">
              {t("adminBroadcasts.targetField")}
            </label>
            <div className="relative mt-1">
              <Link2 size={15}
                     className="absolute left-3 top-1/2 -translate-y-1/2 text-smoke" />
              <input
                id="broadcast-target"
                className="input-base pl-9"
                value={form.target_path}
                maxLength={200}
                placeholder={t("adminBroadcasts.targetPlaceholder")}
                onChange={(e) => set("target_path", e.target.value)}
                list="broadcast-target-suggestions"
                data-testid="broadcast-target"
              />
              <datalist id="broadcast-target-suggestions">
                {["/pricing", "/providers", "/open-jobs", "/how-it-works",
                  "/{role}/dashboard", "/{role}/jobs"].map((p) => (
                    <option key={p} value={p} />
                  ))}
              </datalist>
            </div>
            <p className={`mt-1 text-xs ${targetOk ? "text-smoke" : "text-red-700"}`}
               data-testid="broadcast-target-hint">
              {targetOk ? t("adminBroadcasts.targetHint")
                        : t("adminBroadcasts.targetInvalid")}
            </p>
          </div>

          <div className="mt-5">
            <label className="label-base">{t("adminBroadcasts.audience")}</label>
            <div className="flex flex-wrap gap-2 mt-1">
              {AUDIENCES.map((a) => (
                <button
                  key={a}
                  type="button"
                  onClick={() => { set("audience", a); set("audience_value", ""); }}
                  className={`chip ${form.audience === a ? "bg-hive/20 border-hive text-worker" : ""}`}
                  data-testid={`broadcast-audience-${a}`}
                >
                  {t(`adminBroadcasts.audiences.${a}`)}
                </button>
              ))}
            </div>
          </div>

          {form.audience === "tier" && (
            <div className="mt-3">
              <select
                className="input-base"
                value={form.audience_value}
                onChange={(e) => set("audience_value", e.target.value)}
                data-testid="broadcast-tier"
              >
                <option value="">{t("adminBroadcasts.pickTier")}</option>
                {TIERS.map((tier) => (
                  <option key={tier} value={tier}>Fixy{tier[0].toUpperCase()}{tier.slice(1)}</option>
                ))}
              </select>
            </div>
          )}
          {form.audience === "category" && (
            <div className="mt-3">
              {categoriesState === "error" ? (
                <p className="text-sm text-red-700 flex items-center gap-2 flex-wrap"
                   data-testid="broadcast-categories-error">
                  <AlertCircle size={15} className="shrink-0" />
                  {t("adminBroadcasts.categoriesFailed")}
                  <button type="button" onClick={loadCategories}
                          className="fx-btn fx-btn--ghost text-xs inline-flex items-center gap-1"
                          data-testid="broadcast-categories-retry">
                    <RefreshCw size={13} /> {t("adminBroadcasts.retry")}
                  </button>
                </p>
              ) : (
                <select
                  className="input-base"
                  value={form.audience_value}
                  disabled={categoriesState === "loading"}
                  onChange={(e) => set("audience_value", e.target.value)}
                  data-testid="broadcast-category"
                >
                  <option value="">
                    {categoriesState === "loading"
                      ? t("adminBroadcasts.categoriesLoading")
                      : t("adminBroadcasts.pickCategory")}
                  </option>
                  {categories.map((c) => (
                    <option key={c.category_id} value={c.slug}>{c.name}</option>
                  ))}
                </select>
              )}
            </div>
          )}

          {error && (
            <p className="mt-4 text-sm text-red-700 flex items-start gap-1"
               data-testid="broadcast-error">
              <AlertCircle size={15} className="shrink-0 mt-px" />{error}
            </p>
          )}

          {sent && (
            <p className="mt-4 text-sm text-green-700 flex items-start gap-1"
               data-testid="broadcast-sent">
              <CheckCircle2 size={15} className="shrink-0 mt-px" />
              {t("adminBroadcasts.sent", { n: sent.estimated_recipients })}
            </p>
          )}

          <div className="mt-6 flex items-center gap-3 flex-wrap">
            {estimate === null ? (
              <button
                onClick={ask}
                disabled={busy || !complete}
                className="fx-btn fx-btn--primary"
                data-testid="broadcast-estimate"
              >
                {busy ? <Loader2 size={16} className="animate-spin" /> : <Users size={16} />}
                {t("adminBroadcasts.review")}
              </button>
            ) : (
              <>
                <span className="text-sm text-worker font-display font-semibold"
                      data-testid="broadcast-count">
                  {estimate.verified
                    ? t("adminBroadcasts.confirm",
                        { n: estimate.reachable, total: estimate.registered })
                    : t("adminBroadcasts.confirmUnchecked",
                        { n: estimate.reachable })}
                </span>
                <button
                  onClick={send}
                  disabled={busy || estimate.reachable === 0}
                  className="fx-btn fx-btn--primary"
                  data-testid="broadcast-send"
                >
                  {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                  {t("adminBroadcasts.send")}
                </button>
                <button onClick={() => setEstimate(null)} className="fx-btn fx-btn--ghost text-sm"
                        data-testid="broadcast-cancel">
                  {t("adminBroadcasts.cancel")}
                </button>
              </>
            )}
          </div>
          {estimate !== null && (
            <>
              <Unreachable estimate={estimate} t={t} />
              <ByLanguage estimate={estimate} t={t} />
              <Recipients form={form} t={t} />
            </>
          )}
        </div>

        <h2 className="font-display text-xl font-bold text-worker mt-10 mb-3">
          {t("adminBroadcasts.historyTitle")}
        </h2>
        <div className="card-base overflow-x-auto max-w-4xl">
          <table className="w-full text-sm" data-testid="broadcast-history">
            <thead>
              <tr className="text-left text-xs uppercase tracking-widest text-smoke font-display">
                <th className="p-3">{t("adminBroadcasts.colWhen")}</th>
                <th className="p-3">{t("adminBroadcasts.colWho")}</th>
                <th className="p-3">{t("adminBroadcasts.colWhat")}</th>
                <th className="p-3">{t("adminBroadcasts.colAudience")}</th>
                <th className="p-3">{t("adminBroadcasts.colCount")}</th>
              </tr>
            </thead>
            <tbody>
              {history.length === 0 && (
                <tr><td className="p-3 text-smoke" colSpan={5}>{t("adminBroadcasts.none")}</td></tr>
              )}
              {history.map((b) => (
                <tr key={b.broadcast_id} className="border-t border-sand align-top">
                  <td className="p-3 font-mono text-xs whitespace-nowrap">
                    {formatDateTime(b.created_at) || "—"}
                  </td>
                  <td className="p-3 text-xs">{b.sent_by}</td>
                  <td className="p-3">
                    <div className="font-display font-semibold text-worker">
                      {b.title_en || b.title_es}
                    </div>
                    <div className="text-xs text-smoke">{b.body_en || b.body_es}</div>
                    <div className="text-xs text-smoke mt-1">
                      {(b.title_en ? ["EN"] : []).concat(b.title_es ? ["ES"] : []).join(" · ")}
                      {b.target_path && (
                        <span className="ml-2 font-mono" data-testid="broadcast-row-target">
                          {t("adminBroadcasts.colTarget")} {b.target_path}
                        </span>
                      )}
                    </div>
                    {b.status === "failed" && (
                      <div className="text-xs text-red-700 mt-1">{b.error}</div>
                    )}
                  </td>
                  <td className="p-3 text-xs">
                    {t(`adminBroadcasts.audiences.${b.audience}`)}
                    {b.audience_value ? ` · ${b.audience_value}` : ""}
                  </td>
                  <td className="p-3 font-mono whitespace-nowrap"
                      title={t("adminBroadcasts.countPair", {
                        reachable: b.estimated_recipients,
                        registered: b.registered_devices ?? b.estimated_recipients,
                      })}>
                    {b.estimated_recipients}
                    <span className="text-smoke">
                      {" / "}{b.registered_devices ?? b.estimated_recipients}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pager
          skip={historySkip} limit={HISTORY_PAGE} total={historyTotal}
          onPage={loadHistory} t={t} testid="broadcast-history-pager"
        />
      </div>
    </DashboardLayout>
  );
}

/**
 * Which text each language group is about to read.
 *
 * The fallback is the reason this is on screen. An admin who wrote only Spanish
 * can read "reaches 40 devices" and have no way to tell whether the English
 * speakers were dropped or served the Spanish — and those are opposite
 * outcomes. Shown only when a fallback is actually in play; when both languages
 * were written, every group reads its own and there is nothing to explain.
 */
function ByLanguage({ estimate, t }) {
  const rows = estimate.by_language || [];
  if (!rows.length || (estimate.languages || []).length > 1) return null;
  return (
    <p className="mt-2 text-xs text-smoke" data-testid="broadcast-by-language">
      {rows.map((r) =>
        t("adminBroadcasts.readsIn", {
          n: r.devices,
          prefers: t(`adminBroadcasts.langs.${r.language}`),
          reads: t(`adminBroadcasts.langs.${r.reads}`),
        })
      ).join(" · ")}
    </p>
  );
}

/**
 * The people behind the number, collapsed until asked for.
 *
 * Collapsed because the count is the decision and the list is the check on it,
 * and paged from the server because "everyone" is a legitimate audience: a list
 * that loads all of it is a list that stops working exactly when the broadcast
 * matters most.
 */
function Recipients({ form, t }) {
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(null);
  const [skip, setSkip] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async (next) => {
    setBusy(true); setError("");
    try {
      const { data } = await api.post(
        `/admin/broadcasts/recipients?limit=${RECIPIENTS_PAGE}&skip=${next}`,
        payload(form)
      );
      setPage(data);
      setSkip(next);
    } catch (e) {
      setError(errorMessage(e, t("adminBroadcasts.recipientsFailed")));
    } finally {
      setBusy(false);
    }
  }, [form, t]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && page === null) load(0);
  };

  return (
    <div className="mt-3">
      <button type="button" onClick={toggle}
              className="fx-btn fx-btn--ghost text-sm inline-flex items-center gap-1"
              data-testid="broadcast-recipients-toggle">
        {open ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
        {open ? t("adminBroadcasts.hideRecipients") : t("adminBroadcasts.viewRecipients")}
      </button>

      {open && (
        <div className="mt-2" data-testid="broadcast-recipients">
          {busy && <p className="text-xs text-smoke">{t("adminBroadcasts.loading")}</p>}
          {error && (
            <p className="text-xs text-red-700 flex items-center gap-1">
              <AlertCircle size={13} />{error}
            </p>
          )}
          {page && !busy && !error && (
            <>
              <div className="overflow-x-auto border border-sand rounded-md">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-widest text-smoke font-display">
                      <th className="p-2">{t("adminBroadcasts.colName")}</th>
                      <th className="p-2">{t("adminBroadcasts.colRole")}</th>
                      <th className="p-2">{t("adminBroadcasts.colLang")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {page.recipients.length === 0 && (
                      <tr><td className="p-2 text-smoke" colSpan={3}>
                        {t("adminBroadcasts.noRecipients")}
                      </td></tr>
                    )}
                    {page.recipients.map((r, i) => (
                      <tr key={`${skip}-${i}`} className="border-t border-sand">
                        <td className="p-2">
                          {r.name || <span className="text-smoke">{t("adminBroadcasts.noName")}</span>}
                        </td>
                        <td className="p-2 text-xs">
                          {t(`adminBroadcasts.roles.${r.role || "none"}`)}
                        </td>
                        <td className="p-2 text-xs">
                          {t(`adminBroadcasts.langs.${r.language}`)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pager skip={skip} limit={RECIPIENTS_PAGE} total={page.total}
                     onPage={load} t={t} testid="broadcast-recipients-pager" />
            </>
          )}
        </div>
      )}
    </div>
  );
}

/** Previous / next over a server-side page, or nothing when it all fits. */
function Pager({ skip, limit, total, onPage, t, testid }) {
  if (total <= limit) return null;
  const from = total === 0 ? 0 : skip + 1;
  const to = Math.min(skip + limit, total);
  return (
    <div className="flex items-center gap-3 mt-2 text-xs text-smoke" data-testid={testid}>
      <span>{t("adminBroadcasts.pageOf", { from, to, total })}</span>
      <button type="button" className="fx-btn fx-btn--ghost text-xs" disabled={skip === 0}
              onClick={() => onPage(Math.max(0, skip - limit))}
              data-testid={`${testid}-prev`}>
        {t("adminBroadcasts.prev")}
      </button>
      <button type="button" className="fx-btn fx-btn--ghost text-xs" disabled={to >= total}
              onClick={() => onPage(skip + limit)}
              data-testid={`${testid}-next`}>
        {t("adminBroadcasts.next")}
      </button>
    </div>
  );
}

/**
 * Why the two numbers differ, when they do.
 *
 * Written as a list of reasons rather than a single "unreachable" figure
 * because the reasons are not the same problem. A device with no identity is
 * ours to fix — it re-links itself the next time its owner loads the site with
 * a session (frontend/src/lib/push.js). One that opted out is the person's own
 * decision and nothing to fix. One OneSignal has never heard of is a row we
 * should stop counting. Collapsing all three into one number would hide the
 * only one that is actionable.
 */
function Unreachable({ estimate, t }) {
  const b = estimate.breakdown || {};
  const reasons = ["unidentified", "opted_out", "stale", "unknown"]
    .filter((k) => b[k] > 0)
    .map((k) => t(`adminBroadcasts.reasons.${k}`, { n: b[k] }));

  return (
    <div className="mt-2 space-y-1">
      {estimate.reachable === 0 && (
        <p className="text-xs text-smoke" data-testid="broadcast-empty">
          {estimate.registered === 0
            ? t("adminBroadcasts.empty")
            : t("adminBroadcasts.emptyUnreachable", { total: estimate.registered })}
        </p>
      )}
      {reasons.length > 0 && (
        <p className="text-xs text-smoke" data-testid="broadcast-unreachable">
          {t("adminBroadcasts.excluded")} {reasons.join(" · ")}
        </p>
      )}
      {!estimate.verified && (
        <p className="text-xs text-amber-700" data-testid="broadcast-unverified">
          {t("adminBroadcasts.unverified")}
          {estimate.probe_error ? ` ${estimate.probe_error}` : ""}
          {estimate.unprobed > 0
            ? ` ${t("adminBroadcasts.unprobed", { n: estimate.unprobed })}`
            : ""}
        </p>
      )}
    </div>
  );
}

/**
 * A coded refusal in the admin's own language, or the server's sentence.
 *
 * The three rules the endpoint enforces are rules the person can fix, so they
 * are answered with a code rather than a message: an admin composing in Spanish
 * should not be told about `title_en` in English.
 */
const REFUSALS = {
  broadcast_no_language: "adminBroadcasts.needLanguage",
  broadcast_language_incomplete: "adminBroadcasts.languageIncomplete",
  broadcast_text_too_short: "adminBroadcasts.textTooShort",
  broadcast_target_not_internal: "adminBroadcasts.targetInvalid",
};

function refusal(err, t) {
  const key = REFUSALS[errorCode(err)];
  return key ? t(key) : errorMessage(err, t("adminBroadcasts.failed"));
}

function payload(form) {
  // Empty means "not written in this language" and travels as null, which is
  // what the column holds: an empty string would be a language that exists and
  // says nothing.
  const text = (v) => (v.trim() ? v.trim() : null);
  return {
    title_en: text(form.title_en), title_es: text(form.title_es),
    body_en: text(form.body_en), body_es: text(form.body_es),
    audience: form.audience,
    audience_value: form.audience_value || null,
    target_path: form.target_path.trim() || null,
  };
}

function Pair({ label, en, es, onEn, onEs, maxLength, textarea, testid, t }) {
  const Field = textarea ? "textarea" : "input";
  return (
    <div className="mt-4">
      <label className="label-base">
        {label} <span className="text-smoke font-normal">{t("adminBroadcasts.langOptional")}</span>
      </label>
      <div className="grid md:grid-cols-2 gap-3 mt-1">
        <div>
          <span className="text-xs text-smoke font-display">EN</span>
          <Field
            className="input-base" value={en} maxLength={maxLength}
            rows={textarea ? 3 : undefined}
            onChange={(e) => onEn(e.target.value)}
            data-testid={`${testid}-en`}
          />
        </div>
        <div>
          <span className="text-xs text-smoke font-display">ES</span>
          <Field
            className="input-base" value={es} maxLength={maxLength}
            rows={textarea ? 3 : undefined}
            onChange={(e) => onEs(e.target.value)}
            data-testid={`${testid}-es`}
          />
        </div>
      </div>
    </div>
  );
}
